import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import NotificationToast from "../Helpers/NotificationToast";
import CustomButton from "../Helpers/CustomButton";
import { faClipboard, faRefresh, faFilePdf } from "@fortawesome/pro-regular-svg-icons";

export default function SnagCode() {
    const { current_booking } = useSelector((state) => state.booking);
    const { current_staff, permissions } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(false);
    const [snagCode, setSnagCode] = useState(current_booking?.snag_code || null);
    const [snags, setSnags] = useState([]);
    const [rounds, setRounds] = useState([]);
    const [snagsLoading, setSnagsLoading] = useState(false);
    const [selectedPhoto, setSelectedPhoto] = useState(null);
    const [unitFilter, setUnitFilter] = useState("all");
    const [roundFilter, setRoundFilter] = useState("all");
    const [resetting, setResetting] = useState(false);
    const [pushingToRDrive, setPushingToRDrive] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [togglingSnagId, setTogglingSnagId] = useState(null);
    const [deletingSnagId, setDeletingSnagId] = useState(null);
    const [restoringSnagId, setRestoringSnagId] = useState(null);
    const [showDeleted, setShowDeleted] = useState(false);
    
    // Check if current staff has delete_snags permission
    const canDeleteSnags = permissions?.includes('delete_snags') || false;

    const generateCode = async () => {
        if (!current_booking?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}snagging/generate-code`, {
                booking_id: current_booking.id,
            });
            setSnagCode(data.snag_code);
            toast(<NotificationToast title="Snag Code" message={`Code generated: ${data.snag_code}`} />);
        } catch (e) {
            console.error("Generate snag code failed", e);
            toast(<NotificationToast title="Error" message="Could not generate snag code" type="error" />);
        } finally {
            setLoading(false);
        }
    };

    const copyCode = () => {
        if (!snagCode) return;
        navigator.clipboard.writeText(snagCode);
        toast(<NotificationToast title="Copied" message="Snag code copied to clipboard" />);
    };

    const fetchRounds = useCallback(async () => {
        if (!current_booking?.id) return;
        setSnagsLoading(true);
        try {
            const { data } = await axios.get(`${process.env.REACT_APP_API_URL}snagging/${current_booking.id}/rounds`);
            setRounds(data.rounds || []);
        } catch (e) {
            console.error("Fetch rounds failed", e);
        } finally {
            setSnagsLoading(false);
        }
    }, [current_booking?.id]);

    const fetchSnags = useCallback(async (unitId, roundId) => {
        if (!current_booking?.id || !unitId || !roundId) return;
        setSnagsLoading(true);
        try {
            const params = {
                unit_id: unitId,
                round_id: roundId
            };
            if (showDeleted) {
                params.include_deleted = true;
            }
            const { data } = await axios.get(`${process.env.REACT_APP_API_URL}snagging/${current_booking.id}/snags`, { params });
            setSnags(data.snags || []);
        } catch (e) {
            console.error("Fetch snags failed", e);
        } finally {
            setSnagsLoading(false);
        }
    }, [current_booking?.id, showDeleted]);

    useEffect(() => {
        setSnagCode(current_booking?.snag_code || null);
        setSnags([]);
        setRounds([]);
        setUnitFilter("all");
        setRoundFilter("all");
        fetchRounds();
    }, [current_booking?.id, current_booking?.snag_code, fetchRounds]);

    // Auto-select first unit when data loads
    useEffect(() => {
        const unitIds = [...new Set(rounds.map((r) => r.unit_id).filter(Boolean))];
        if (unitIds.length > 0 && unitFilter === "all") {
            setUnitFilter(String(unitIds[0]));
        }
    }, [rounds]);

    // Auto-select first round when unit changes
    useEffect(() => {
        if (unitFilter !== "all" && roundFilter === "all") {
            const firstRound = rounds.find((r) => String(r.unit_id) === unitFilter);
            if (firstRound) setRoundFilter(String(firstRound.id));
        }
    }, [unitFilter, rounds]);

    // Fetch snags when both unit and round are selected
    useEffect(() => {
        if (unitFilter !== "all" && roundFilter !== "all") {
            fetchSnags(unitFilter, roundFilter);
        } else {
            setSnags([]);
        }
    }, [unitFilter, roundFilter, fetchSnags]);

    const filteredSnags = useMemo(() => {
        return snags.filter((snag) => {
            const isDeleted = !!snag.deleted_at;
            // When showDeleted is true, only show deleted snags
            // When showDeleted is false, only show active snags
            const matchesDeletedFilter = showDeleted ? isDeleted : !isDeleted;
            return matchesDeletedFilter;
        });
    }, [snags, showDeleted]);

    const selectedRound = rounds.find((r) => String(r.id) === roundFilter);
    const isSuperUser = current_staff?.isSuper === 1;

    const resetRound = async () => {
        if (!selectedRound) return;
        if (!window.confirm("Reset this round to pending? This will unlock all snags and clear the signature/happy letter.")) return;
        setResetting(true);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/reset`);
            toast(<NotificationToast title="Round Reset" message="Round has been reset to pending" />);
            fetchRounds();
            if (unitFilter !== "all" && roundFilter !== "all") {
                fetchSnags(unitFilter, roundFilter);
            }
        } catch (e) {
            console.error("Reset round failed", e);
            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not reset round"} type="error" />);
        } finally {
            setResetting(false);
        }
    };

    const downloadSnagsPdf = async () => {
        if (unitFilter === "all") {
            toast(<NotificationToast title="Select Unit" message="Please select a unit to download snags" type="warning" />);
            return;
        }

        setDownloadingPdf(true);
        try {
            // Use optimized direct download endpoint
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}snagging/${current_booking.id}/download-pdf`,
                {
                    unit_id: parseInt(unitFilter),
                    round_id: roundFilter !== "all" ? parseInt(roundFilter) : null,
                },
                { responseType: 'blob' }
            );

            // Create download link
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            
            const contentDisposition = response.headers['content-disposition'];
            let fileName = 'Snags.pdf';
            if (contentDisposition) {
                const fileNameMatch = contentDisposition.match(/filename="(.+)"/);
                if (fileNameMatch && fileNameMatch.length === 2) {
                    fileName = fileNameMatch[1];
                }
            }
            
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast(<NotificationToast title="PDF Downloaded" message="Snags PDF downloaded successfully" />);
        } catch (e) {
            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not download PDF"} type="error" />);
        } finally {
            setDownloadingPdf(false);
        }
    };

    const toggleSnagStatus = async (snagId, currentStatus) => {
        const newStatus = currentStatus === "open" ? "resolved" : "open";
        setTogglingSnagId(snagId);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}snagging/snags/${snagId}/toggle-status`, {
                status: newStatus,
            });
            toast(<NotificationToast title="Status Updated" message={`Snag marked as ${newStatus}`} />);
            if (unitFilter !== "all" && roundFilter !== "all") {
                fetchSnags(unitFilter, roundFilter);
            }
        } catch (e) {
            console.error("Toggle snag status failed", e);
            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not update snag status"} type="error" />);
        } finally {
            setTogglingSnagId(null);
        }
    };

    const deleteSnag = async (snagId) => {
        if (!window.confirm("Are you sure you want to delete this snag? It will be hidden but can be restored.")) return;
        
        setDeletingSnagId(snagId);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}snagging/snags/${snagId}/delete`);
            toast(<NotificationToast title="Snag Deleted" message="Snag has been deleted (can be restored)" />);
            if (unitFilter !== "all" && roundFilter !== "all") {
                fetchSnags(unitFilter, roundFilter);
            }
        } catch (e) {
            console.error("Delete snag failed", e);
            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not delete snag"} type="error" />);
        } finally {
            setDeletingSnagId(null);
        }
    };

    const restoreSnag = async (snagId) => {
        setRestoringSnagId(snagId);
        try {
            await axios.post(`${process.env.REACT_APP_API_URL}snagging/snags/${snagId}/restore`);
            toast(<NotificationToast title="Snag Restored" message="Snag has been restored successfully" />);
            if (unitFilter !== "all" && roundFilter !== "all") {
                fetchSnags(unitFilter, roundFilter);
            }
        } catch (e) {
            console.error("Restore snag failed", e);
            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not restore snag"} type="error" />);
        } finally {
            setRestoringSnagId(null);
        }
    };

    const permanentDeleteSnag = async (snagId) => {
        if (!window.confirm("⚠️ PERMANENT DELETE\n\nThis will permanently delete the snag AND its image from storage.\n\nThis action CANNOT be undone!\n\nAre you absolutely sure?")) return;
        
        setDeletingSnagId(snagId);
        try {
            await axios.delete(`${process.env.REACT_APP_API_URL}snagging/snags/${snagId}/permanent-delete`);
            toast(<NotificationToast title="Permanently Deleted" message="Snag and image permanently removed" />);
            if (unitFilter !== "all" && roundFilter !== "all") {
                fetchSnags(unitFilter, roundFilter);
            }
        } catch (e) {
            console.error("Permanent delete snag failed", e);
            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not permanently delete snag"} type="error" />);
        } finally {
            setDeletingSnagId(null);
        }
    };

    const pushToRDrive = async () => {
        if (!selectedRound) return;
        
        const debugLog = (message, data = null) => {
            if (current_staff?.show_dev_stuff) {
                console.log(`[RDrive Push] ${message}`, data || '');
            }
        };

        debugLog('Starting RDrive push', {
            roundId: selectedRound.id,
            roundLabel: selectedRound.label,
            unitId: selectedRound.unit_id,
            unitName: selectedRound.unit_name,
            snagCount: selectedRound.snag_count,
            status: selectedRound.status
        });

        if (!window.confirm(`Push ${selectedRound.snag_count || 0} snag(s) from "${selectedRound.label}" to RDrive?\n\nThis will create Consultant Issues in RDrive for all unsynced snags.`)) {
            debugLog('Push cancelled by user');
            return;
        }

        setPushingToRDrive(true);
        debugLog('Sending API request...');

        try {
            const startTime = Date.now();
            const { data } = await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/push-to-rdrive`);
            const duration = Date.now() - startTime;

            debugLog('API response received', {
                duration: `${duration}ms`,
                success: data.success,
                syncedCount: data.synced_count,
                totalCount: data.total_count,
                errors: data.errors,
                fullResponse: data
            });

            if (data.success || data.status === 'queued') {
                const message = data.message || `Synced ${data.synced_count || 0} snag(s) to RDrive`;
                const title = data.status === 'queued' ? 'RDrive Sync Started' : 'RDrive Sync Complete';
                toast(<NotificationToast title={title} message={message} type="info" />);
                
                if (data.errors && data.errors.length > 0) {
                    debugLog('Sync completed with errors', data.errors);
                    toast(<NotificationToast 
                        title="Partial Sync" 
                        message={`${data.errors.length} snag(s) failed to sync. Check console for details.`} 
                        type="warning" 
                    />);
                }

                // Refresh snags to show updated sync status
                debugLog('Refreshing snags list...');
                if (unitFilter !== "all" && roundFilter !== "all") {
                    fetchSnags(unitFilter, roundFilter);
                }
            } else {
                debugLog('Sync failed', data);
                toast(<NotificationToast 
                    title="Sync Failed" 
                    message={data.error || "Could not sync to RDrive"} 
                    type="error" 
                />);
            }
        } catch (e) {
            debugLog('API request failed', {
                error: e.message,
                response: e.response?.data,
                status: e.response?.status,
                fullError: e
            });

            console.error("Push to RDrive failed", e);
            
            const errorMessage = e.response?.data?.error || e.response?.data?.message || e.message || "Could not push to RDrive";
            toast(<NotificationToast 
                title="RDrive Push Failed" 
                message={errorMessage} 
                type="error" 
            />);

            // Show detailed error for dev users
            if (current_staff?.show_dev_stuff && e.response?.data) {
                debugLog('Detailed error response', e.response.data);
            }
        } finally {
            setPushingToRDrive(false);
            debugLog('Push operation completed');
        }
    };

    const formatDate = (isoDate) => {
        try {
            const dt = new Date(isoDate);
            return dt.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
        } catch {
            return "";
        }
    };

    const formatShortDate = (isoDate) => {
        try {
            const dt = new Date(isoDate);
            return dt.toLocaleDateString("en-ZA", { day: "numeric", month: "short", year: "numeric" });
        } catch {
            return "";
        }
    };

    const getRoundDisplayStatus = (round) => {
        if (round.status === "completed") return { label: "COMPLETED", bg: "bg-green-50", text: "text-green-600", dot: "bg-green-500" };
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (round.date_from) {
            const from = new Date(round.date_from);
            from.setHours(0, 0, 0, 0);
            if (now < from) {
                const days = Math.ceil((from - now) / 86400000);
                return { label: `UPCOMING · ${days}d`, bg: "bg-blue-50", text: "text-blue-600", dot: "bg-blue-400" };
            }
        }
        if (round.date_to) {
            const to = new Date(round.date_to);
            to.setHours(23, 59, 59, 999);
            if (now > to) return { label: "CLOSED", bg: "bg-red-50", text: "text-red-600", dot: "bg-red-400" };
            const days = Math.ceil((to - now) / 86400000);
            return { label: `ACTIVE · ${days}d left`, bg: "bg-orange-50", text: "text-orange-600", dot: "bg-orange-400" };
        }
        return { label: "ACTIVE", bg: "bg-orange-50", text: "text-orange-600", dot: "bg-orange-400" };
    };

    // Derive unique units from rounds (primary) and snags (fallback)
    const uniqueUnits = rounds.reduce((acc, r) => {
        if (r.unit_id && !acc.find((u) => u.id === r.unit_id)) {
            acc.push({ id: r.unit_id, name: r.unit_name || r.unit_id });
        }
        return acc;
    }, []);
    // Also add units from snags that may not have rounds
    snags.forEach((s) => {
        if (s.unit_id && !uniqueUnits.find((u) => u.id === s.unit_id)) {
            uniqueUnits.push({ id: s.unit_id, name: s.unit_name || s.unit_id });
        }
    });

    // Rounds filtered by selected unit (empty if no unit chosen)
    const unitRounds = unitFilter === "all" ? [] : rounds.filter((r) => String(r.unit_id) === unitFilter);

    return (
        <div>
            {snagCode ? (
                <div>
                    <p className="text-xs text-gray-500 mb-1">Snag Code</p>
                    <div className="flex items-center space-x-2">
                        <span className="font-mono text-2xl font-bold tracking-widest text-gray-800 bg-white px-4 py-2 rounded-lg border border-gray-200 select-all">
                            {snagCode}
                        </span>
                        <CustomButton text="Copy" icon={faClipboard} onClick={copyCode} styling="text-xs" />
                    </div>
                    <p className="text-xs text-gray-400 mt-2">Share this code with the person doing the snagging</p>
                </div>
            ) : (
                <div>
                    <p className="text-sm text-gray-500 mb-2">No snag code generated yet for this booking.</p>
                    <CustomButton text="Generate Snag Code" icon={faRefresh} onClick={generateCode} loading={loading ? true : undefined} styling="w-full" />
                </div>
            )}

            {/* Unit Filter */}
            {uniqueUnits.length > 0 && (
                <div className="mt-4">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Unit</p>
                    <select
                        value={unitFilter}
                        onChange={(e) => { setUnitFilter(e.target.value); setRoundFilter("all"); }}
                        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                    >
                        <option value="all">Choose a unit...</option>
                        {uniqueUnits.map((u) => (
                            <option key={u.id} value={String(u.id)}>Unit {u.name}</option>
                        ))}
                    </select>
                </div>
            )}

            {/* Round Filter */}
            {unitRounds.length > 0 && (
                <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Round</p>
                    <select
                        value={roundFilter}
                        onChange={(e) => setRoundFilter(e.target.value)}
                        className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-300"
                    >
                        <option value="all">Choose a round...</option>
                        {unitRounds.map((r) => {
                            const ds = getRoundDisplayStatus(r);
                            return (
                                <option key={r.id} value={String(r.id)}>
                                    {r.label} — {ds.label}{r.snag_count ? ` (${r.snag_count} snags)` : ""}
                                </option>
                            );
                        })}
                    </select>
                </div>
            )}

            {/* Round Date Range */}
            {selectedRound && (
                <div className="mt-3 bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <div className="flex items-center justify-between mb-2">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Round Dates</p>
                        {selectedRound.dates_customized && (
                            <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium">
                                Customized
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[10px] text-gray-400 uppercase tracking-wide">From</label>
                            <input
                                type="date"
                                value={selectedRound.date_from || ""}
                                onChange={async (e) => {
                                    try {
                                        await axios.put(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}`, {
                                            date_from: e.target.value || null,
                                        });
                                        toast(<NotificationToast title="Updated" message="Round start date updated" />);
                                        fetchSnags();
                                    } catch (err) {
                                        console.error("Update date failed", err);
                                        toast(<NotificationToast title="Error" message={err.response?.data?.error || "Could not update date"} type="error" />);
                                    }
                                }}
                                className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300"
                            />
                        </div>
                        <div>
                            <label className="text-[10px] text-gray-400 uppercase tracking-wide">To</label>
                            <input
                                type="date"
                                value={selectedRound.date_to || ""}
                                onChange={async (e) => {
                                    try {
                                        await axios.put(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}`, {
                                            date_to: e.target.value || null,
                                        });
                                        toast(<NotificationToast title="Updated" message="Round end date updated" />);
                                        fetchSnags();
                                    } catch (err) {
                                        console.error("Update date failed", err);
                                        toast(<NotificationToast title="Error" message={err.response?.data?.error || "Could not update date"} type="error" />);
                                    }
                                }}
                                className="w-full text-xs border border-gray-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-300"
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* Defects Completion Status */}
            {selectedRound && (
                <div className="mt-3 flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Defects Complete</span>
                    <div className="flex items-center space-x-2">
                        {isSuperUser ? (
                            <div className="flex items-center space-x-1">
                                <button
                                    onClick={async () => {
                                        try {
                                            await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/toggle-defects`, { value: 'null' });
                                            toast(<NotificationToast title="Updated" message="Defects set to Pending" />);
                                            fetchSnags();
                                        } catch (e) {
                                            console.error("Toggle defects failed", e);
                                            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not update"} type="error" />);
                                        }
                                    }}
                                    className={`text-xs font-semibold px-2 py-0.5 rounded cursor-pointer transition ${
                                        selectedRound.defects_completed === null || selectedRound.defects_completed === undefined
                                            ? "bg-gray-100 text-gray-600 ring-2 ring-gray-400"
                                            : "bg-gray-50 text-gray-400 hover:bg-gray-100"
                                    }`}
                                    title="Set to Pending"
                                >
                                    Pending
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/toggle-defects`, { value: 'true' });
                                            toast(<NotificationToast title="Updated" message="Defects set to Yes" />);
                                            fetchSnags();
                                        } catch (e) {
                                            console.error("Toggle defects failed", e);
                                            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not update"} type="error" />);
                                        }
                                    }}
                                    className={`text-xs font-semibold px-2 py-0.5 rounded cursor-pointer transition ${
                                        selectedRound.defects_completed === true || selectedRound.defects_completed === 1
                                            ? "bg-orange-50 text-orange-600 ring-2 ring-orange-400"
                                            : "bg-orange-50 text-orange-300 hover:text-orange-500"
                                    }`}
                                    title="Set to Yes"
                                >
                                    Yes
                                </button>
                            </div>
                        ) : (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                selectedRound.defects_completed
                                    ? "bg-orange-50 text-orange-600"
                                    : "bg-gray-50 text-gray-400"
                            }`}>
                                {selectedRound.defects_completed ? "Yes" : "Pending"}
                            </span>
                        )}
                    </div>
                </div>
            )}

            {/* Unit Inspection Signed Status & Reset */}
            {selectedRound?.happy_letter_required && (
                <div className="mt-3 flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Unit Inspection Signed</span>
                    <div className="flex items-center space-x-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                            selectedRound.status === "completed"
                                ? "bg-green-50 text-green-600"
                                : "bg-gray-50 text-gray-400"
                        }`}>
                            {selectedRound.status === "completed" ? "Complete" : "Pending"}
                        </span>
                        {selectedRound.status === "completed" && isSuperUser && (
                            <button
                                onClick={async () => {
                                    if (!window.confirm("Reset unit inspection to pending? This will clear the signature and happy letter.")) return;
                                    try {
                                        await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/reset-inspection`);
                                        toast(<NotificationToast title="Inspection Reset" message="Unit inspection has been reset to pending" />);
                                        fetchSnags();
                                    } catch (e) {
                                        console.error("Reset inspection failed", e);
                                        toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not reset inspection"} type="error" />);
                                    }
                                }}
                                className="text-gray-400 hover:text-red-600 transition"
                                title="Reset to pending"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Key Choice */}
            {selectedRound && selectedRound.key_handover_required ? (
                <div className="mt-3 flex items-center justify-between bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Key Received</span>
                    <div className="flex items-center space-x-2">
                        {isSuperUser ? (
                            <div className="flex items-center space-x-1">
                                <button
                                    onClick={async () => {
                                        try {
                                            await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/toggle-key`, { value: 'null' });
                                            toast(<NotificationToast title="Updated" message="Key set to Pending" />);
                                            fetchSnags();
                                        } catch (e) {
                                            console.error("Toggle key failed", e);
                                            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not update"} type="error" />);
                                        }
                                    }}
                                    className={`text-xs font-semibold px-2 py-0.5 rounded cursor-pointer transition ${
                                        selectedRound.key_received === null || selectedRound.key_received === undefined
                                            ? "bg-gray-100 text-gray-600 ring-2 ring-gray-400"
                                            : "bg-gray-50 text-gray-400 hover:bg-gray-100"
                                    }`}
                                    title="Set to Pending"
                                >
                                    Pending
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/toggle-key`, { value: 'false' });
                                            toast(<NotificationToast title="Updated" message="Key set to No" />);
                                            fetchSnags();
                                        } catch (e) {
                                            console.error("Toggle key failed", e);
                                            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not update"} type="error" />);
                                        }
                                    }}
                                    className={`text-xs font-semibold px-2 py-0.5 rounded cursor-pointer transition ${
                                        selectedRound.key_received === false || selectedRound.key_received === 0
                                            ? "bg-red-50 text-red-600 ring-2 ring-red-400"
                                            : "bg-red-50 text-red-300 hover:text-red-500"
                                    }`}
                                    title="Set to No"
                                >
                                    No
                                </button>
                                <button
                                    onClick={async () => {
                                        try {
                                            await axios.post(`${process.env.REACT_APP_API_URL}snagging/rounds/${selectedRound.id}/toggle-key`, { value: 'true' });
                                            toast(<NotificationToast title="Updated" message="Key set to Yes" />);
                                            fetchSnags();
                                        } catch (e) {
                                            console.error("Toggle key failed", e);
                                            toast(<NotificationToast title="Error" message={e.response?.data?.error || "Could not update"} type="error" />);
                                        }
                                    }}
                                    className={`text-xs font-semibold px-2 py-0.5 rounded cursor-pointer transition ${
                                        selectedRound.key_received === true || selectedRound.key_received === 1
                                            ? "bg-green-50 text-green-600 ring-2 ring-green-400"
                                            : "bg-green-50 text-green-300 hover:text-green-500"
                                    }`}
                                    title="Set to Yes"
                                >
                                    Yes
                                </button>
                            </div>
                        ) : (
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                                selectedRound.key_received === true || selectedRound.key_received === 1
                                    ? "bg-green-50 text-green-600"
                                    : selectedRound.key_received === false || selectedRound.key_received === 0
                                        ? "bg-red-50 text-red-600"
                                        : "bg-gray-50 text-gray-400"
                            }`}>
                                {selectedRound.key_received === true || selectedRound.key_received === 1
                                    ? "Yes"
                                    : selectedRound.key_received === false || selectedRound.key_received === 0
                                        ? "No"
                                        : "Pending"}
                            </span>
                        )}
                    </div>
                </div>
            ) : null}

            {/* Download PDF Button */}
            {unitFilter !== "all" && filteredSnags.length > 0 && (
                <div className="mt-3">
                    <CustomButton
                        text="Download Snags PDF"
                        icon={faFilePdf}
                        onClick={downloadSnagsPdf}
                        loading={downloadingPdf ? 1 : 0}
                        styling="w-full"
                    />
                </div>
            )}

            {/* Push to RDrive (dev users only) */}
            {current_staff?.show_dev_stuff && selectedRound && selectedRound.snag_count > 0 && (
                <div className="mt-3">
                    <button
                        onClick={pushToRDrive}
                        disabled={pushingToRDrive}
                        className="w-full text-xs font-medium px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50 transition flex items-center justify-center space-x-2"
                    >
                        {pushingToRDrive ? (
                            <>
                                <svg className="animate-spin h-3 w-3 text-blue-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <span>Pushing to RDrive...</span>
                            </>
                        ) : (
                            <>
                                <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                <span>Push to RDrive</span>
                            </>
                        )}
                    </button>
                    {current_staff?.show_dev_stuff && (
                        <p className="text-[10px] text-gray-400 mt-1 text-center">
                            Dev only • Creates Consultant Issues in RDrive
                        </p>
                    )}
                </div>
            )}

            {/* Snags List */}
            <div className="mt-3">
                <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Snags {unitFilter !== "all" && roundFilter !== "all" && snags.length > 0 && `(${filteredSnags.length})`}
                    </p>
                    <div className="flex items-center space-x-2">
                        {canDeleteSnags && (
                            <button 
                                onClick={() => {
                                    setShowDeleted(!showDeleted);
                                }} 
                                className={`text-xs font-medium px-2 py-1 rounded transition ${
                                    showDeleted 
                                        ? "bg-red-100 text-red-700 hover:bg-red-200" 
                                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                                }`}
                            >
                                {showDeleted ? "Hide Deleted" : "Show Deleted"}
                            </button>
                        )}
                        <button 
                            onClick={() => {
                                if (unitFilter !== "all" && roundFilter !== "all") {
                                    fetchSnags(unitFilter, roundFilter);
                                } else {
                                    fetchRounds();
                                }
                            }} 
                            disabled={snagsLoading} 
                            className="text-xs text-blue-500 hover:text-blue-700 disabled:text-gray-300"
                        >
                            {snagsLoading ? "Loading..." : "Refresh"}
                        </button>
                    </div>
                </div>

                {(unitFilter === "all" || roundFilter === "all") ? (
                    <div className="text-center py-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-400">{unitFilter === "all" ? "Select a unit to view snags" : "Select a round to view snags"}</p>
                    </div>
                ) : snagsLoading ? (
                    <div className="text-center py-4">
                        <p className="text-xs text-gray-400">Loading snags...</p>
                    </div>
                ) : filteredSnags.length === 0 ? (
                    <div className="text-center py-3 bg-white rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-400">No snags reported yet</p>
                    </div>
                ) : (
                    <div className="space-y-2 max-h-96 overflow-y-auto">
                        {filteredSnags.map((snag) => {
                            const isDeleted = !!snag.deleted_at;
                            return (
                            <div key={snag.uuid} className={`rounded-lg border overflow-hidden ${
                                isDeleted 
                                    ? "bg-red-50 border-red-300 opacity-75" 
                                    : "bg-white border-gray-200"
                            }`}>
                                {snag.photo_url && (
                                    <img
                                        src={snag.photo_url}
                                        alt="Snag"
                                        className="w-full h-32 object-cover cursor-pointer hover:opacity-90 transition"
                                        onClick={() => setSelectedPhoto(snag.photo_url)}
                                    />
                                )}
                                <div className="p-3">
                                    <p className="text-sm text-gray-800 leading-snug">{snag.comment}</p>
                                    <div className="flex items-center justify-between mt-2">
                                        <div className="flex items-center space-x-1.5 flex-wrap gap-y-1">
                                            {isDeleted && (
                                                <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                                                    DELETED
                                                </span>
                                            )}
                                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                                snag.status === "open"
                                                    ? "bg-red-50 text-red-600"
                                                    : "bg-green-50 text-green-600"
                                            }`}>
                                                {snag.status?.toUpperCase()}
                                            </span>
                                            {snag.round_label && (
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                                    snag.round_status === "completed"
                                                        ? "bg-blue-50 text-blue-600"
                                                        : "bg-purple-50 text-purple-600"
                                                }`}>
                                                    {snag.round_label}
                                                </span>
                                            )}
                                            {current_staff?.show_dev_stuff && snag.rdrive_issue_id && (
                                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                                    snag.rdrive_sync_status === "synced"
                                                        ? "bg-green-50 text-green-600"
                                                        : snag.rdrive_sync_status === "partial"
                                                            ? "bg-yellow-50 text-yellow-600"
                                                            : "bg-gray-50 text-gray-600"
                                                }`}>
                                                    {snag.rdrive_sync_status === "synced" ? "✓ RDrive" : snag.rdrive_sync_status === "partial" ? "⚠ RDrive" : "○ RDrive"}
                                                </span>
                                            )}
                                            {current_staff?.show_dev_stuff && snag.rdrive_sync_error && (
                                                <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-red-50 text-red-600" title={snag.rdrive_sync_error}>
                                                    ✕ RDrive Error
                                                </span>
                                            )}
                                        </div>
                                        <span className="text-[10px] text-gray-400 whitespace-nowrap ml-2">{formatDate(snag.created_at)}</span>
                                    </div>
                                    <div className="flex items-center space-x-2 mt-2">
                                        {!isDeleted ? (
                                            <>
                                                <button
                                                    onClick={() => toggleSnagStatus(snag.id, snag.status)}
                                                    disabled={togglingSnagId === snag.id}
                                                    className={`text-xs font-medium px-2 py-1 rounded transition ${
                                                        snag.status === "open"
                                                            ? "bg-green-100 text-green-700 hover:bg-green-200"
                                                            : "bg-amber-100 text-amber-700 hover:bg-amber-200"
                                                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                                                >
                                                    {togglingSnagId === snag.id ? (
                                                        <span className="flex items-center space-x-1">
                                                            <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            <span>...</span>
                                                        </span>
                                                    ) : snag.status === "open" ? (
                                                        "Mark Resolved"
                                                    ) : (
                                                        "Reopen"
                                                    )}
                                                </button>
                                                {canDeleteSnags && (
                                                    <button
                                                        onClick={() => deleteSnag(snag.id)}
                                                        disabled={deletingSnagId === snag.id}
                                                        className="text-xs font-medium px-2 py-1 rounded transition bg-orange-100 text-orange-700 hover:bg-orange-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Soft delete - can be restored"
                                                    >
                                                        {deletingSnagId === snag.id ? (
                                                            <span className="flex items-center space-x-1">
                                                                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                <span>...</span>
                                                            </span>
                                                        ) : (
                                                            "Delete"
                                                        )}
                                                    </button>
                                                )}
                                            </>
                                        ) : (
                                            // Deleted snag - show restore + permanent delete
                                            canDeleteSnags && (
                                                <>
                                                    <button
                                                        onClick={() => restoreSnag(snag.id)}
                                                        disabled={restoringSnagId === snag.id}
                                                        className="text-xs font-medium px-2 py-1 rounded transition bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Restore this snag"
                                                    >
                                                        {restoringSnagId === snag.id ? (
                                                            <span className="flex items-center space-x-1">
                                                                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                <span>...</span>
                                                            </span>
                                                        ) : (
                                                            "Restore"
                                                        )}
                                                    </button>
                                                    <button
                                                        onClick={() => permanentDeleteSnag(snag.id)}
                                                        disabled={deletingSnagId === snag.id}
                                                        className="text-xs font-medium px-2 py-1 rounded transition bg-red-100 text-red-700 hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title="Permanent delete - removes image from storage"
                                                    >
                                                        {deletingSnagId === snag.id ? (
                                                            <span className="flex items-center space-x-1">
                                                                <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                                <span>...</span>
                                                            </span>
                                                        ) : (
                                                            "⚠ Permanent Delete"
                                                        )}
                                                    </button>
                                                </>
                                            )
                                        )}
                                    </div>
                                </div>
                            </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Photo Lightbox */}
            {selectedPhoto && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-80 z-50 flex items-center justify-center cursor-pointer"
                    onClick={() => setSelectedPhoto(null)}
                >
                    <img
                        src={selectedPhoto}
                        alt="Snag photo"
                        className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                    />
                    <button
                        className="absolute top-4 right-4 text-white text-3xl font-light hover:text-gray-300"
                        onClick={() => setSelectedPhoto(null)}
                    >
                        &times;
                    </button>
                </div>
            )}
        </div>
    );
}
